import { useRef, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { KEY_RECOIL, TYPE_RHYTHM_EVENT_ENUM } from '@/shared/config';
import { convertToMomentJs, isRrInterval } from '@/shared/lib/aiUtils';
import { fetchHolterEvents, formatEventStrip, updateEventChangeAndNew } from '@/entities/event';
import {
  selectActiveTab,
  selectDailyCount,
  selectEventChanges,
  selectEventNews,
  selectSelectedStrip,
  useReportStore,
} from '@/entities/report';
import type { NewEventsProps } from './newEvents';

/**
 * Drives the modal listing the ECG events added locally but not yet saved, with per-strip preview and removal. Subscribes to the tab-scoped new events, active event tab, selected strip and thumbnail render settings, tracks which row a pending removal targets, and exposes handlers to cancel, confirm a removal and jump the report to a chosen event.
 *
 * @param props - Modal props carrying the study and profile ids, the timezone offset and the onCancel callback.
 * @returns An object carrying the tab key, new events, active tab, selected strip, confirm-dialog state, the pending removal index ref, the thumbnail channel, high-pass and gain settings, and the cancel, remove, confirm-remove and select handlers.
 */
const useNewEvents = ({
  onCancel,
  timezoneOffset,
  studyId,
  profileId,
}: Pick<NewEventsProps, 'onCancel' | 'timezoneOffset' | 'studyId' | 'profileId'>) => {
  const keyRecoil = KEY_RECOIL.BEAT_HR;
  const eventNews = useReportStore(selectEventNews(keyRecoil));
  const setEventNewsAction = useReportStore((s) => s.actions.setEventNews);
  /**
   * Writes the locally added events for the beats tab back into the report store. Forwards the value or updater to the store action with the tab key already bound.
   *
   * @param v - Replacement list of new events, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setEventNews = (v: Parameters<typeof setEventNewsAction>[1]) =>
    setEventNewsAction(keyRecoil, v);
  /**
   * Reads the pending ECG event edits for the beats tab out of the report store. Applies the tab-scoped selectEventChanges selector to a fresh store snapshot instead of subscribing to it.
   *
   * @returns The list of edited events awaiting save.
   */
  const getEventChanges = () => selectEventChanges(keyRecoil)(useReportStore.getState());
  const setEventChangesAction = useReportStore((s) => s.actions.setEventChanges);
  /**
   * Writes the pending event edits for the beats tab back into the report store. Forwards the value or updater to the store action with the tab key already bound.
   *
   * @param v - Replacement list of edited events, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setEventChanges = (v: Parameters<typeof setEventChangesAction>[1]) =>
    setEventChangesAction(keyRecoil, v);
  const setSelectedDateValueAction = useReportStore((s) => s.actions.setSelectedDateValue);
  /**
   * Writes the beats tab's selected study day back into the report store. Forwards the value or updater to the store action with the tab key already bound.
   *
   * @param v - Replacement selected date, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setSelectedDateValue = (v: Parameters<typeof setSelectedDateValueAction>[1]) =>
    setSelectedDateValueAction(keyRecoil, v);
  /**
   * Reads the per-day strip counts for the beats tab out of the report store. Applies the tab-scoped selectDailyCount selector to a fresh store snapshot instead of subscribing to it.
   *
   * @returns The daily strip-count entries for the study.
   */
  const getDailyCount = () => selectDailyCount(keyRecoil)(useReportStore.getState());
  const setIsEmptyDataAction = useReportStore((s) => s.actions.setIsEmptyData);
  /**
   * Writes the beats tab's empty-data flag back into the report store. Forwards the value or updater to the store action with the tab key already bound.
   *
   * @param v - Replacement empty-data flag, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setIsEmptyData = (v: Parameters<typeof setIsEmptyDataAction>[1]) =>
    setIsEmptyDataAction(keyRecoil, v);
  const setReloadEcgViewerAction = useReportStore((s) => s.actions.setReloadEcgViewer);
  /**
   * Writes the beats tab's ECG viewer reload counter back into the report store. Forwards the value or updater to the store action with the tab key already bound.
   *
   * @param v - Replacement reload counter, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setReloadEcgViewer = (v: Parameters<typeof setReloadEcgViewerAction>[1]) =>
    setReloadEcgViewerAction(keyRecoil, v);
  const activeButton = useReportStore(selectActiveTab(keyRecoil));
  const setActiveButtonAction = useReportStore((s) => s.actions.setActiveTab);
  /**
   * Writes the beats tab's active event tab back into the report store. Forwards the value or updater to the store action with the tab key already bound.
   *
   * @param v - Replacement active tab, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setActiveButton = (v: Parameters<typeof setActiveButtonAction>[1]) =>
    setActiveButtonAction(keyRecoil, v);
  const setPageIndexAction = useReportStore((s) => s.actions.setPageIndex);
  /**
   * Writes the beats tab's strip pagination index back into the report store. Forwards the value or updater to the store action with the tab key already bound.
   *
   * @param v - Replacement page index, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setPageIndex = (v: Parameters<typeof setPageIndexAction>[1]) =>
    setPageIndexAction(keyRecoil, v);
  const setTotalStrip = useReportStore((s) => s.actions.setTotalStrip);
  const setEcgObjectAction = useReportStore((s) => s.actions.setEcgObject);
  /**
   * Writes the beats tab's cached ECG object back into the report store. Forwards the value or updater to the store action with the tab key already bound.
   *
   * @param v - Replacement ECG object, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setEcgObject = (v: Parameters<typeof setEcgObjectAction>[1]) =>
    setEcgObjectAction(keyRecoil, v);
  const setCombinedEventsInHeatmap = useReportStore((s) => s.actions.setCombinedEventsInHeatmap);
  const channelsThumbnail = useReportStore((s) => s.channelsThumbnail);
  const highPassThumbnail = useReportStore((s) => s.highPassThumbnail);
  const gainThumbnail = useReportStore((s) => s.gainThumbnail);

  const selectedStrip = useReportStore(selectSelectedStrip(keyRecoil));
  const setSelectedStripAction = useReportStore((s) => s.actions.setSelectedStrip);
  /**
   * Writes the beats tab's selected strip back into the report store. Forwards the value or updater to the store action with the tab key already bound.
   *
   * @param v - Replacement event, beat or ECG bookmark, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setSelectedStrip = (v: Parameters<typeof setSelectedStripAction>[1]) =>
    setSelectedStripAction(keyRecoil, v);
  const [isOpenConfirmRemove, setIsOpenConfirmRemove] = useState(false);
  const stripRemoveIndex = useRef<number | undefined>(undefined);
  const setEventOptionsAction = useReportStore((s) => s.actions.setEventOptions);
  /**
   * Records an event-option snapshot for the beats tab so the removal can be undone and redone as one step. Forwards the value or updater to the store action with the tab key already bound.
   *
   * @param v - Replacement options payload, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setEventOptions = (v: Parameters<typeof setEventOptionsAction>[1]) =>
    setEventOptionsAction(keyRecoil, v);

  /**
   * Closes the new-events review modal without touching the pending events. Delegates straight to the onCancel callback supplied by the parent.
   *
   * @returns Nothing.
   */
  const onClickCancel = () => {
    onCancel();
  };

  /**
   * Starts the removal of one locally added event strip. Parks the row's index in a ref and opens the confirmation dialog rather than deleting immediately.
   *
   * @param index - Position of the strip inside the list of newly added events.
   * @returns Nothing.
   */
  const onClickRemoveStrips = (index: number) => {
    stripRemoveIndex.current = index;
    setIsOpenConfirmRemove(true);
  };

  /**
   * Deletes the confirmed new-event strip and every event change that was linked to it. Drops the row at the parked index, filters out the linked event changes, and when the active tab is the RR-interval heatmap refetches that day's artifact events to rebuild its combined-events entry, then records an undo snapshot, commits the new events and event changes, closes the dialog and nudges the ECG viewer to reload.
   *
   * @returns A promise resolving once the strip is removed and the ECG viewer reload has been triggered.
   */
  const onClickConfirmRemoveStrip = async () => {
    const updatedEventNews = [...eventNews];
    const eventToDelete: any = updatedEventNews[stripRemoveIndex.current as number];
    const linkEvents = eventToDelete?.linkEvents || [];
    const filterEventChanges = getEventChanges().filter((eventChange) => {
      if (linkEvents.find((event: any) => event.idEvent === eventChange.idEvent)) {
        return false;
      }
      return true;
    });
    updatedEventNews.splice(stripRemoveIndex.current as number, 1);
    if (isRrInterval(activeButton as any)) {
      //* Bên tab heatmap không có hr chart nên phải fetch riêng artifact để show trên ecg viewer
      //* Lúc thay đổi phải update lại mảng event
      const dateContainEvent = convertToMomentJs(eventToDelete.start, timezoneOffset)
        .startOf('days')
        .toISOString();
      const filter = {
        studyId,
        profileId,
        types: [TYPE_RHYTHM_EVENT_ENUM.ARTIFACT],
        startSearchDate: dateContainEvent,
        stopSearchDate: moment(dateContainEvent).add(1, 'days').toISOString(),
      };
      const { events } = await fetchHolterEvents(filter, 0, true, null, false);
      setCombinedEventsInHeatmap((prev: any) => {
        const clonePrev: any = { ...prev };
        clonePrev[dateContainEvent] = updateEventChangeAndNew(
          formatEventStrip(events),
          filterEventChanges,
          updatedEventNews,
          filter.startSearchDate,
          filter.stopSearchDate,
          [TYPE_RHYTHM_EVENT_ENUM.ARTIFACT],
        );
        return clonePrev;
      });
    }
    // Perform grouping event action applicable to undo/redo
    setEventOptions((prevOptions: any) => ({
      ...prevOptions,
      eventNewsState: updatedEventNews,
      eventChangesState: filterEventChanges,
    }));
    setEventNews(updatedEventNews);
    setEventChanges(filterEventChanges);
    stripRemoveIndex.current = undefined;
    setIsOpenConfirmRemove(false);
    setReloadEcgViewer((prev: any) => prev + _.round(Math.random() * 100));
  };

  /**
   * Jumps the report to a newly added event when its row is clicked. Skips the work if that event is already selected, otherwise selects the strip, switches the active event tab to its type and clears the empty-data flag, then for AFib clears the selected date while for other types it resets to page zero and aligns the selected date and total strip count with the matching daily-count entry.
   *
   * @param event - Newly added event whose row was clicked.
   * @returns Nothing.
   */
  const handleClickSelectEvent = (event: any) => {
    //* View event new sẽ nhảy tới tab của event đó và nhảy tới thời gian có event new
    if (event.id !== selectedStrip?.id) {
      // resetEcgViewerConfigStore(););
      setSelectedStrip(event);
      setActiveButton(event.type);
      setIsEmptyData(false);
      if (event.type === TYPE_RHYTHM_EVENT_ENUM.AFIB) {
        setSelectedDateValue(null);
      } else {
        setPageIndex((prev) => (prev.index === 0 ? prev : { index: 0 }));
        const foundSelectEventDailyCount = _.find(
          getDailyCount(),
          (x) =>
            convertToMomentJs(x.date, timezoneOffset).format('DD-MM-YYYY') ===
            convertToMomentJs(event.start, timezoneOffset).format('DD-MM-YYYY'),
        );
        setSelectedDateValue(foundSelectEventDailyCount?.date);
        setTotalStrip(foundSelectEventDailyCount?.count ?? 0);
        setEcgObject({});
      }
    }
  };

  return {
    keyRecoil,
    eventNews,
    activeButton,
    selectedStrip,
    isOpenConfirmRemove,
    setIsOpenConfirmRemove,
    stripRemoveIndex,
    channelsThumbnail,
    highPassThumbnail,
    gainThumbnail,
    onClickCancel,
    onClickRemoveStrips,
    onClickConfirmRemoveStrip,
    handleClickSelectEvent,
  };
};

export default useNewEvents;
